import type { AdvancedCandidateTableField } from '~/types/advanced-candidate-table';
import type { JobQuestion, User } from '~/types/models';
import { JobApplicationComment } from '~/types/models';

export const state = () => ({
	fields: [
		{
			key: 'match',
			label: 'Match',
			type: 'string',
			sortable: true,
			hidden: false,
			filter_key: 'Match',
			filterable_by_values: true,
			filter_options: null,
		},
		{
			key: 'status',
			label: 'Status',
			type: 'string',
			sortable: true,
			hidden: false,
			filter_key: 'Status',
			filterable_by_values: true,
			filter_options: null,
		},
		{
			key: 'strength',
			label: 'Strength',
			type: 'string',
			sortable: true,
			hidden: false,
			filter_key: 'Strength',
			filterable_by_values: true,
			filter_options: null,
		},
		{
			key: 'created_at',
			label: 'Applied Date',
			type: 'date',
			sortable: true,
			hidden: false,
			filter_key: 'Applied Date',
			filterable_by_values: true,
			filter_options: ['From Date', 'To Date'],
		},
		{
			key: 'assignees',
			label: 'Assignees',
			type: 'object',
			sortable: false,
			hidden: true,
			filter_key: 'Assignee',
			filterable_by_values: true,
			filter_options: null,
		},
		{
			key: 'cover_letter.link',
			label: 'Cover Letter',
			type: 'link',
			sortable: false,
			hidden: true,
			filter_key: 'Cover Letter',
			filterable_by_values: false,
			filter_options: null,
		},
		{
			key: 'general_preferences.contact_method',
			label: 'Preferred Contact Method',
			type: 'string',
			sortable: true,
			hidden: true,
			filter_key: 'Contact Method',
			filterable_by_values: true,
			filter_options: null,
		},
		{
			key: 'general_preferences.earliest_start_date',
			label: 'Earliest Start Date',
			type: 'date',
			sortable: true,
			hidden: true,
			filter_key: 'Earliest Start Date',
			filterable_by_values: true,
			filter_options: null,
			// date
		},
		{
			key: 'general_preferences.job_type',
			label: 'Preferred Job Type',
			type: 'string',
			sortable: true,
			hidden: true,
			filter_key: 'Job Type',
			filterable_by_values: true,
			filter_options: null,
		},
		{
			key: 'general_preferences.location',
			label: 'Preferred Location Type',
			type: 'string',
			sortable: true,
			hidden: true,
			filter_key: 'Location Type',
			filterable_by_values: true,
			filter_options: null,
		},
		{
			key: 'general_preferences.relocate',
			label: 'Willing to Relocate',
			type: 'string',
			sortable: true,
			hidden: true,
			filter_key: 'Relocate',
			filterable_by_values: true,
			filter_options: ['Yes', 'No'],
		},
		{
			key: 'general_preferences.formatted_notice_period',
			label: 'Notice Period',
			type: 'string',
			sortable: false,
			hidden: true,
			filter_key: 'Notice Period',
			filterable_by_values: false,
			filter_options: null,
		},
		{
			key: 'profile.about_me',
			label: 'About',
			type: 'string',
			sortable: false,
			hidden: true,
			filter_key: 'About',
			filterable_by_values: false,
			filter_options: null,
		},
		{
			key: 'profile.has_drivers_license',
			label: 'Has Drivers License',
			type: 'string',
			sortable: true,
			hidden: true,
			filter_key: 'Drivers License',
			filterable_by_values: true,
			filter_options: ['Yes', 'No'],
		},
		{
			key: 'profile.has_own_transport',
			label: 'Has Own Transport',
			type: 'string',
			sortable: true,
			hidden: true,
			filter_key: 'Own Transport',
			filterable_by_values: true,
			filter_options: ['Yes', 'No'],
		},
		{
			key: 'profile.interests',
			label: 'Interests',
			type: 'object',
			sortable: false,
			hidden: true,
			filter_key: 'Interest',
			filterable_by_values: true,
			filter_options: null,
		},
		{
			key: 'profile.preferred_language',
			label: 'Preferred Language',
			type: 'string',
			sortable: true,
			hidden: true,
			filter_key: 'Language',
			filterable_by_values: true,
			filter_options: null,
		},
		{
			key: 'prospected',
			label: 'Prospected',
			type: 'string',
			sortable: true,
			hidden: true,
			filter_key: 'Prospected',
			filterable_by_values: true,
			filter_options: ['Yes', 'No'],
		},
		{
			key: 'resume.link',
			label: 'Resume',
			type: 'link',
			sortable: false,
			hidden: true,
			filter_key: 'Resume',
			filterable_by_values: false,
			filter_options: null,
		},
		{
			key: 'shortlisted',
			label: 'Shortlisted',
			type: 'string',
			sortable: true,
			hidden: true,
			filter_key: 'Resume',
			filterable_by_values: true,
			filter_options: ['Yes', 'No'],
		},
		{
			key: 'skills',
			label: 'Skills',
			type: 'object',
			sortable: false,
			hidden: true,
			filter_key: 'Skill',
			filterable_by_values: true,
			filter_options: null,
		},
		{
			key: 'qualifications',
			label: 'Qualifications',
			type: 'object',
			sortable: false,
			hidden: true,
			filter_key: 'Qualification',
			filterable_by_values: true,
			filter_options: null,
		},
		{
			key: 'universities',
			label: 'Universities',
			type: 'object',
			sortable: false,
			hidden: true,
			filter_key: 'University',
			filterable_by_values: true,
			filter_options: null,
		},
	],
	applicants: [],
	comments: [] as JobApplicationComment[],
	contactMethods: [],
	interests: [],
	jobTypes: [],
	languages: [],
	locationTypes: [],
	matchTypes: [],
	qualifications: [],
	skills: [],
	statusTypes: [],
	strengthTypes: [],
	universities: [],
});

export const mutations = {
	setApplicants(state: any, payload: any): void {
		state.applicants = payload;
	},

	setComments(state: any, payload: JobApplicationComment[]) {
		state.comments = payload;
	},

	updateApplicants(state: any, payload: any): void {
		if (!state.applicants) return;

		state.applicants.data.map(function (applicant) {
			if (applicant.uuid === payload.uuid) {
				Object.assign(applicant, payload);
			}
			return applicant;
		});
	},

	setFields(state: any, payload: AdvancedCandidateTableField[]): void {
		state.fields = payload;
	},

	setFieldFilterableValues(state: any, payload: any): void {
		state.fields.forEach((field) => {
			switch (field.key) {
				case 'match':
					field.filter_options = state.matchTypes;
					break;
				case 'status':
					field.filter_options = state.statusTypes;
					break;
				case 'strength':
					field.filter_options = state.strengthTypes;
					break;
				case 'general_preferences.contact_method':
					field.filter_options = state.contactMethods;
					break;
				case 'general_preferences.job_type':
					field.filter_options = state.jobTypes;
					break;
				case 'general_preferences.location':
					field.filter_options = state.locationTypes;
					break;
				case 'profile.interests':
					field.filter_options = state.interests;
					break;
				case 'profile.preferred_language':
					field.filter_options = state.languages;
					break;
				case 'skills':
					field.filter_options = state.skills;
					break;
				case 'qualifications':
					field.filter_options = state.qualifications;
					break;
				case 'universities':
					field.filter_options = state.universities;
					break;
				default:
					break;
			}
		});
	},

	setUniqueInterests(state: any, applications: any): void {
		state.interests = [
			...applications.reduce((acc, { profile: { interests } }) => {
				interests?.forEach(({ name }) => acc.add(name.replace(/^\w/, (c) => c.toUpperCase())));
				return acc;
			}, new Set()),
		];
	},

	setUniqueMatchTypes(state: any, applications: any): void {
		state.matchTypes = [...new Set(applications.map((i) => i.match))];
	},

	setUniqueQualifications(state: any, applications: any): void {
		state.qualifications = [
			...applications.reduce((acc, { qualifications }) => {
				qualifications?.forEach(({ name }) => acc.add(name.replace(/^\w/, (c) => c.toUpperCase())));
				return acc;
			}, new Set()),
		];
	},

	setUniqueSkills(state: any, applications: any): void {
		state.skills = [
			...applications.reduce((acc, { skills }) => {
				skills?.forEach(({ name }) => acc.add(name.replace(/^\w/, (c) => c.toUpperCase())));
				return acc;
			}, new Set()),
		];
	},

	setUniqueStatusTypes(state: any, applications: any): void {
		state.statusTypes = [...new Set(applications.map((i) => i.status))];
	},

	setUniqueStrengthTypes(state: any, applications: any): void {
		state.strengthTypes = [...new Set(applications.map((i) => i.strength))];
	},

	setUniqueUniversities(state: any, applications: any): void {
		state.universities = [
			...applications.reduce((acc, { universities }) => {
				universities.forEach(({ name }) => acc.add(name));
				return acc;
			}, new Set()),
		];
	},

	setUniqueContactMethods(state: any, applications: any): void {
		state.contactMethods = [
			...applications.reduce((acc, { general_preferences }) => {
				general_preferences.contact_method && acc.add(general_preferences.contact_method);
				return acc;
			}, new Set()),
		];
	},

	setUniqueJobTypes(state: any, applications: any): void {
		state.jobTypes = [
			...applications.reduce((acc, { general_preferences }) => {
				general_preferences.job_type && acc.add(general_preferences.job_type);
				return acc;
			}, new Set()),
		];
	},

	setUniqueLanguages(state: any, applications: any): void {
		state.languages = [
			...applications.reduce((acc, { profile }) => {
				profile.preferred_language && acc.add(profile.preferred_language);
				return acc;
			}, new Set()),
		];
	},

	setUniqueLocationTypes(state: any, applications: any): void {
		state.locationTypes = [
			...applications.reduce((acc, { general_preferences }) => {
				general_preferences.location && acc.add(general_preferences.location);
				return acc;
			}, new Set()),
		];
	},
};
export const getters = {
	getApplicants: (state: any) => state.applicants,
	getComments: (state: any): JobApplicationComment[] => state.comments,
	getContactMethods: (state: any): string[] => state.contactMethods,
	getFields: (state: any): AdvancedCandidateTableField[] => state.fields,
	getInterests: (state: any): string[] => state.interests,
	getJobTypes: (state: any): string[] => state.jobTypes,
	getLanguages: (state: any): string[] => state.languages,
	getLocationTypes: (state: any): string[] => state.locationTypes,
	getMatchTypes: (state: any): string[] => state.matchTypes,
	getQualifications: (state: any): string[] => state.qualifications,
	getSkills: (state: any): string[] => state.skills,
	getStatusTypes: (state: any): string[] => state.statusTypes,
	getStrengthTypes: (state: any): string[] => state.strengthTypes,
	getUniversities: (state: any): string[] => state.universities,
};

export const actions = {
	addComment(state: any, payload: any) {
		const { user_uuid, ...data } = payload;

		return new Promise((resolve, reject) => {
			this.$axios
				.post(`/api/users/${user_uuid}/job-application-comments`, data)
				.then((res) => {
					const applications = state.state.applicants.map((applicant) =>
						payload.application_ids.includes(applicant.job_application_uuid)
							? { ...applicant, comments_count: applicant.comments_count + 1 }
							: applicant,
					);

					state.commit('setApplicants', applications);
					resolve(res.data);
				})
				.catch((err) => {
					reject(err);
				});
		});
	},

	addJobApplicationsToProspectList(state: any, payload: any) {
		return new Promise((resolve, reject) => {
			this.$axios
				.post(`/api/jobs/applications/bulk-actions/add-to-prospect-list`, payload)
				.then((res) => {
					const applications = state.state.applicants.map((applicant) =>
						payload.application_ids.includes(applicant.job_application_uuid) ? { ...applicant, prospected: 'Yes' } : applicant,
					);

					state.commit('setApplicants', applications);
					resolve(res.data);
				})
				.catch((err) => {
					reject(err);
				});
		});
	},

	addJobApplicationsToShortlist(state: any, payload: any) {
		return new Promise((resolve, reject) => {
			this.$axios
				.post(`/api/jobs/applications/bulk-actions/add-to-shortlist`, payload)
				.then((res) => {
					const applications = state.state.applicants.map((applicant) =>
						payload.application_ids.includes(applicant.job_application_uuid) ? { ...applicant, shortlisted: 'Yes' } : applicant,
					);

					state.commit('setApplicants', applications);
					resolve(res.data);
				})
				.catch((err) => {
					reject(err);
				});
		});
	},

	addQuestionFields(state: any, questions: JobQuestion[]) {
		const existingFields = state.state.fields.slice();

		const questionFields = [
			...questions
				.map(({ input_type, question, options }, index) => {
					let filter_options = null;
					let type = 'string';

					if (input_type === 'Date') {
						type = 'date';
						filter_options = ['From Date', 'To Date'];
					}

					if (input_type === 'Date & Time') {
						type = 'datetime';
						filter_options = ['From Date', 'To Date'];
					}

					if (input_type.includes('Number')) {
						filter_options = Array.from({ length: 10 }, (_, i) => `${i + 1}+`);
					}

					if (input_type.includes('Single Choice')) {
						filter_options = options.map(({ text }) => text);
					}

					if (input_type.includes('Yes/No')) {
						filter_options = ['Yes', 'No'];
					}

					return {
						key: `answers[${index}].text`,
						label: question,
						sortable: true,
						hidden: true,
						filter_key: `Question ${index + 1}`,
						filterable_by_values: !input_type.includes('Text'),
						type,
						filter_options,
					};
				})
				.filter((i) => !existingFields.some((j) => j.key === i.key)),
		];

		state.commit('setFields', [...existingFields, ...questionFields]);
	},

	associateJobApplicationsWithUser(state: any, payload: any) {
		const { user, ...data } = payload;

		return new Promise((resolve, reject) => {
			this.$axios
				.post(`/api/users/${user.uuid}/job-applications`, data)
				.then((res) => {
					const applications = state.state.applicants.map((applicant) =>
						payload.application_ids.includes(applicant.job_application_uuid)
							? { ...applicant, assignees: applicant.assignees.concat(user) }
							: applicant,
					);

					state.commit('setApplicants', applications);

					state.commit('setApplicants', applications);
					resolve(res.data);
				})
				.catch((err) => {
					reject(err);
				});
		});
	},

	disassociateJobApplicationsFromUser(state: any, payload: any) {
		const { user, ...data } = payload;

		return new Promise((resolve, reject) => {
			this.$axios
				.delete(`/api/users/${user.uuid}/job-applications`, { data })
				.then((res) => {
					const applications = state.state.applicants.map((applicant) =>
						payload.application_ids.includes(applicant.job_application_uuid)
							? { ...applicant, assignees: applicant.assignees.filter((assignee) => assignee.uuid !== user.uuid) }
							: applicant,
					);

					state.commit('setApplicants', applications);
					resolve(res.data);
				})
				.catch((err) => {
					reject(err);
				});
		});
	},

	getComments(state: any, job_application_uuid: any) {
		return new Promise((resolve, reject) => {
			this.$axios
				.get(`/api/jobs/applications/${job_application_uuid}/comments`)
				.then((res) => {
					state.commit('setComments', res.data);
					resolve(res.data);
				})
				.catch((err) => {
					reject(err);
				});
		});
	},

	getJobApplicants(state: any, job_uuid: string) {
		return new Promise((resolve, reject) => {
			this.$axios
				.get(`/api/jobs/${job_uuid}/advanced-application-filtering`)
				.then((res) => {
					state.commit('setApplicants', res.data);
					state.commit('setUniqueContactMethods', res.data);
					state.commit('setUniqueInterests', res.data);
					state.commit('setUniqueJobTypes', res.data);
					state.commit('setUniqueLanguages', res.data);
					state.commit('setUniqueLocationTypes', res.data);
					state.commit('setUniqueMatchTypes', res.data);
					state.commit('setUniqueQualifications', res.data);
					state.commit('setUniqueSkills', res.data);
					state.commit('setUniqueStatusTypes', res.data);
					state.commit('setUniqueStrengthTypes', res.data);
					state.commit('setUniqueUniversities', res.data);
					state.commit('setFieldFilterableValues');

					resolve(res.data);
				})
				.catch((err) => {
					reject(err);
				});
		});
	},

	removeJobApplicationsFromShortlist(state: any, payload: any) {
		const { ...data } = payload;

		return new Promise((resolve, reject) => {
			this.$axios
				.delete(`/api/jobs/applications/bulk-actions/remove-from-shortlist`, { data })
				.then((res) => {
					const applications = state.state.applicants.map((applicant) =>
						payload.application_ids.includes(applicant.job_application_uuid) ? { ...applicant, shortlisted: 'No' } : applicant,
					);

					state.commit('setApplicants', applications);
					resolve(res.data);
				})
				.catch((err) => {
					reject(err);
				});
		});
	},

	rejectJobApplications(state: any, payload: any) {
		return new Promise((resolve, reject) => {
			this.$axios
				.post(`/api/jobs/applications/bulk-actions/reject`, payload)
				.then((res) => {
					const applications = state.state.applicants.map((applicant) =>
						payload.application_ids.includes(applicant.job_application_uuid) ? { ...applicant, status: 'Rejected' } : applicant,
					);

					if (!state.state.statusTypes.includes('Rejected')) {
						state.commit('setUniqueStatusTypes', applications);
					}

					state.commit('setApplicants', applications);
					resolve(res.data);
				})
				.catch((err) => {
					reject(err);
				});
		});
	},

	removeJobApplicationsFromProspectList(state: any, payload: any) {
		const { ...data } = payload;

		return new Promise((resolve, reject) => {
			this.$axios
				.delete(`/api/jobs/applications/bulk-actions/remove-from-prospect-list`, { data })
				.then((res) => {
					const applications = state.state.applicants.map((applicant) =>
						payload.application_ids.includes(applicant.job_application_uuid) ? { ...applicant, prospected: 'No' } : applicant,
					);

					state.commit('setApplicants', applications);
					resolve(res.data);
				})
				.catch((err) => {
					reject(err);
				});
		});
	},

	setApplicants(state: any, payload: any) {
		state.commit('setApplicants', payload);
	},

	setFields(state: any, fields: AdvancedCandidateTableField[] | null) {
		if (!fields) return;

		state.commit('setFields', fields);
	},

	setAssigneesFieldOptions(state: any, users: User[]) {
		const fields = state.state.fields.map((field) => {
			return field.key === 'assignees' ? { ...field, filter_options: users.map((user) => user.name) } : field;
		});

		state.commit('setFields', fields);
	},

	toggleFieldVisibility(state: any, key: string): void {
		let fields = state.state.fields.map((field) => (field.key === key ? { ...field, hidden: !field.hidden } : field));

		state.commit('setFields', fields);
	},
};
